/**
 * Mailchimp's web version of a newsletter (campaign-archive.com, the "View
 * this email in your browser" page) wraps the email in an archive page: a
 * `<body id="archivebody">` holding the bar `<div id="awesomewrap">` (Share,
 * Past Issues, Translate, 50+ links), then the email's own `<body>`, and after
 * the email's `</body>` the bar's scripts (jQuery and a click handler) and a
 * second `</body>`. A stylesheet link (`.../css/archivebar-desktop.css`) sits in
 * the head. None of it is part of the email, and the scripts alone make the
 * server check refuse the import. Measured on NoPain's 20 archived newsletters
 * (2024-12 to 2026-09): every one has exactly this shape.
 *
 * Removes only what is recognisably the archive page. Without the bar marker,
 * or when the bar's `<div>` never closes, the HTML is returned unchanged
 * (no guessing). The trailing scripts are removed only when everything between
 * the email's `</body>` and the archive's `</body>` is scripts and whitespace.
 */

const BAR_START = /<div\s+id\s*=\s*["']?awesomewrap["']?[^>]*>/i;
const DIV_TAG = /<(\/?)div\b[^>]*>/;
const ARCHIVE_BODY_BEFORE_BAR = /<body\s+id\s*=\s*["']?archivebody["']?[^>]*>\s*$/i;
const ARCHIVE_BAR_STYLESHEET = /<link\b[^>]*\barchivebar[^>]*>/gi;
const BODY_END = /<\/body\s*>/gi;
const SCRIPT_ELEMENT = /<script\b[^>]*>[\s\S]*?<\/script\s*>/gi;

export interface ArchiveBarResult {
  html: string;
  /** True when the bar was found and removed. */
  removed: boolean;
}

/** Strips Mailchimp's archive-page bar, its scripts and its stylesheet from `html`; see the module comment. */
export function stripMailchimpArchiveBar(html: string): ArchiveBarResult {
  const start = BAR_START.exec(html);
  if (!start) return { html, removed: false };

  let depth = 0;
  let barEnd = -1;
  const divTags = new RegExp(DIV_TAG.source, 'gi');
  divTags.lastIndex = start.index;
  for (let tag = divTags.exec(html); tag; tag = divTags.exec(html)) {
    depth += tag[1] ? -1 : 1;
    if (depth === 0) {
      barEnd = tag.index + tag[0].length;
      break;
    }
  }
  if (barEnd === -1) return { html, removed: false };

  // The archive's own `<body id="archivebody">` goes with the bar when nothing but whitespace separates them.
  const before = html.slice(0, start.index);
  const archiveBody = ARCHIVE_BODY_BEFORE_BAR.exec(before);
  const cutFrom = archiveBody ? archiveBody.index : start.index;
  let out = before.slice(0, cutFrom) + html.slice(barEnd).replace(/^[ \t]*/, '');

  // After the email's `</body>`: the bar's scripts, then the archive's `</body>`.
  const bodyEnds = [...out.matchAll(BODY_END)];
  if (bodyEnds.length >= 2) {
    const emailEnd = bodyEnds[bodyEnds.length - 2]!;
    const archiveEnd = bodyEnds[bodyEnds.length - 1]!;
    const tailFrom = emailEnd.index + emailEnd[0].length;
    const between = out.slice(tailFrom, archiveEnd.index);
    if (between.replace(SCRIPT_ELEMENT, '').trim() === '') {
      out = out.slice(0, tailFrom) + out.slice(archiveEnd.index + archiveEnd[0].length);
    }
  }

  out = out.replace(ARCHIVE_BAR_STYLESHEET, '');
  return { html: out, removed: true };
}
